<script setup>
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  visible: { type: Boolean, default: false },
  notebook: { type: Object, required: true },
})

const emit = defineEmits(['update:visible'])

const { t } = useI18n()

const isVisible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value),
})

const aboutOpen = ref(false)
const copied = ref(false)
const promptBox = useTemplateRef('promptBox')
let copyResetTimer = null

// V2 "Kompaktne" of the RED-291 prototype: 6 steps, the first carries the
// notebook link inline, the fourth embeds the copyable sample prompt.
const steps = computed(() => [
  { n: 1, link: true },
  { n: 2, text: t('messages.detail.notebook.step2') },
  { n: 3, text: t('messages.detail.notebook.step3') },
  { n: 4, text: t('messages.detail.notebook.step4'), prompt: true },
  { n: 5, text: t('messages.detail.notebook.step5') },
  { n: 6, text: t('messages.detail.notebook.step6') },
])

const copyPrompt = async () => {
  let done = false

  try {
    await navigator.clipboard.writeText(props.notebook.sample_prompt)
    done = true
  } catch {
    // Clipboard API unavailable (permissions, non-secure context) — select
    // the prompt text and try the legacy path so manual Ctrl+C also works.
    const node = Array.isArray(promptBox.value) ? promptBox.value[0] : promptBox.value
    const selection = node ? window.getSelection() : null
    if (node && selection) {
      const range = document.createRange()
      range.selectNodeContents(node)
      selection.removeAllRanges()
      selection.addRange(range)
      done = document.execCommand('copy')
    }
  }

  if (done) {
    copied.value = true
    clearTimeout(copyResetTimer)
    copyResetTimer = setTimeout(() => (copied.value = false), 2000)
  }
}

onBeforeUnmount(() => clearTimeout(copyResetTimer))
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :draggable="false"
    dismissable-mask
    :style="{ width: '40rem', maxWidth: '95vw' }"
    :pt="{ root: { 'data-testid': 'notebook-help-dialog' } }"
  >
    <template #header>
      <div class="flex items-start gap-3 pr-2">
        <span class="pi pi-sparkles mt-1 text-lg text-blue-600"></span>
        <span class="text-lg font-semibold leading-snug text-slate-900">
          {{ $t('messages.detail.notebook.title') }}
        </span>
      </div>
    </template>

    <div class="flex flex-col gap-5">
      <!-- Collapsible about-section (collapsed by default) -->
      <div class="overflow-hidden rounded-xl border border-slate-200">
        <button
          type="button"
          data-testid="notebook-about-toggle"
          class="flex w-full items-center justify-between gap-3 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700"
          @click="aboutOpen = !aboutOpen"
        >
          <span>{{ $t('messages.detail.notebook.aboutToggle') }}</span>
          <span class="pi text-xs text-slate-400" :class="aboutOpen ? 'pi-chevron-up' : 'pi-chevron-down'"></span>
        </button>
        <div v-if="aboutOpen" class="flex flex-col gap-3 border-t border-slate-200 p-4">
          <p class="m-0 text-sm leading-relaxed text-slate-600">{{ $t('messages.detail.notebook.aboutP1') }}</p>
          <p class="m-0 text-sm leading-relaxed text-slate-600">{{ $t('messages.detail.notebook.aboutP2') }}</p>
        </div>
      </div>

      <!-- Step cards -->
      <div class="flex flex-col gap-2.5">
        <div
          v-for="step in steps"
          :key="step.n"
          class="flex items-start gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3"
        >
          <span class="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-600">
            {{ step.n }}
          </span>
          <div class="flex-1 text-sm leading-relaxed text-slate-600">
            <template v-if="step.link">
              <a
                :href="notebook.url"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="notebook-step-link"
                class="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1 text-sm font-semibold text-white hover:bg-blue-700 hover:text-white"
              >
                <span class="pi pi-external-link text-xs"></span>
                {{ $t('messages.detail.notebook.step1Button') }}
              </a>
              <span> {{ $t('messages.detail.notebook.step1Suffix') }}</span>
            </template>
            <template v-else>
              {{ step.text }}
              <div v-if="step.prompt" class="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <div
                  ref="promptBox"
                  data-testid="notebook-sample-prompt"
                  class="whitespace-pre-wrap px-4 py-3 font-mono text-xs leading-relaxed text-slate-700"
                >{{ notebook.sample_prompt }}</div>
                <div class="flex justify-end border-t border-slate-200 bg-white px-3 py-2">
                  <button
                    type="button"
                    data-testid="notebook-copy-prompt"
                    class="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
                    @click="copyPrompt"
                  >
                    <span class="pi text-xs" :class="copied ? 'pi-check' : 'pi-copy'"></span>
                    {{ copied ? $t('messages.detail.notebook.copied') : $t('messages.detail.notebook.copy') }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Guide (PDF) link -->
      <div class="text-sm leading-relaxed text-slate-500">
        {{ $t('messages.detail.notebook.guideText') }}
        <a
          href="/docs/kinnitamise-kiirendi-juhend.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="notebook-guide-link"
          class="whitespace-nowrap font-semibold"
        >
          {{ $t('messages.detail.notebook.guideLink') }} <span class="pi pi-external-link text-xs"></span>
        </a>
      </div>

      <!-- Access / caution / AI-limits info -->
      <div class="flex flex-col gap-2.5 border-t border-slate-100 pt-4">
        <div class="flex items-start gap-2.5">
          <span class="pi pi-lock mt-0.5 text-sm text-slate-400"></span>
          <span class="text-sm leading-relaxed text-slate-500">{{ $t('messages.detail.notebook.infoAccess') }}</span>
        </div>
        <div class="flex items-start gap-2.5">
          <span class="pi pi-exclamation-triangle mt-0.5 text-sm text-amber-600"></span>
          <span class="text-sm leading-relaxed text-slate-500">{{ $t('messages.detail.notebook.infoCaution') }}</span>
        </div>
        <div class="flex items-start gap-2.5">
          <span class="pi pi-info-circle mt-0.5 text-sm text-slate-400"></span>
          <span class="text-sm leading-relaxed text-slate-500">{{ $t('messages.detail.notebook.infoLimits') }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-between gap-4">
        <Button
          :label="$t('messages.detail.notebook.close')"
          text
          severity="secondary"
          data-testid="notebook-close"
          @click="isVisible = false"
        />
        <Button
          as="a"
          :href="notebook.url"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="notebook-cta"
        >
          {{ $t('messages.detail.notebook.cta') }}
          <span class="pi pi-external-link text-xs"></span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>
